import React, { useEffect, useState, useCallback } from 'react';
import { getAllEmailMessages, getAllEmails, sendBulkEmail } from '../../services/EmailService';
import { getAllEmailTemplatesDropdown, getAllEmailTemplates } from '../../services/TemplatesService';
import { Link } from 'react-router-dom';
import EmailHistorySidebar from './EmailHistorySidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Pagination from '../../utils/Pagination';

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function EmailMessages() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarEmail, setSidebarEmail] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [newEmailModalOpen, setNewEmailModalOpen] = useState(false);
  
  // New email form state
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [status, setStatus] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Search and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDescending, setSortDescending] = useState(true);

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    recipientEmail: '',
    senderEmail: '',
    subject: '',
    status: '',
    emailTemplateId: '',
    createdFrom: '',
    createdTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    recipientEmail: '',
    senderEmail: '',
    subject: '',
    status: '',
    emailTemplateId: '',
    createdFrom: '',
    createdTo: '',
  });

  // Sync tempFilters when sidebar opens
  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // Count active filters
  const activeFilterCount = Object.values(appliedFilters).filter(v => v !== '').length;

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 2000);

  // Fetch templates for dropdown
  useEffect(() => {
    async function fetchTemplates() {
      try {
        const data = await getAllEmailTemplatesDropdown();
        setTemplates(data);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    }
    fetchTemplates();
  }, []);

  // Fetch templates for new email form
  useEffect(() => {
    async function fetchEmailTemplates() {
      try {
        const data = await getAllEmailTemplates();
        setEmailTemplates(data);
      } catch (err) {
        toast.error('Failed to load templates');
      }
    }
    if (newEmailModalOpen) {
      fetchEmailTemplates();
    }
  }, [newEmailModalOpen]);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        pageNumber: currentPage,
        pageSize,
        sortBy,
        sortDescending,
      };

      if (appliedFilters.recipientEmail) params.recipientEmail = appliedFilters.recipientEmail;
      if (appliedFilters.senderEmail) params.senderEmail = appliedFilters.senderEmail;
      if (appliedFilters.subject) params.subject = appliedFilters.subject;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.emailTemplateId) params.emailTemplateId = appliedFilters.emailTemplateId;
      if (appliedFilters.createdFrom) params.createdFrom = appliedFilters.createdFrom;
      if (appliedFilters.createdTo) params.createdTo = appliedFilters.createdTo;

      const data = await getAllEmailMessages(params);
      setEmails(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError('Failed to load emails');
      setEmails([]);
      setTotalItems(0);
    }
    setLoading(false);
  }, [currentPage, pageSize, sortBy, sortDescending, appliedFilters]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Filter handlers
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    const emptyFilters = {
      recipientEmail: '',
      senderEmail: '',
      subject: '',
      status: '',
      emailTemplateId: '',
      createdFrom: '',
      createdTo: '',
    };
    setTempFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const handleViewHistory = (email) => {
    setSidebarEmail(email);
    setSidebarOpen(true);
  };

  const handlePreviewEmail = (email) => {
    setPreviewEmail(email);
    setPreviewOpen(true);
  };

  const openBlockModal = (address, isBlocked) => {
    if (isBlocked) {
      toast.info(`Unblock Email: ${address} (This is in progress)`, { position: 'top-right', autoClose: 2500 });
    } else {
      toast.success(`Block Email: ${address} (This is in progress)`, { position: 'top-right', autoClose: 2500 });
    }
  };

  const getStatusBadgeClass = (status = '') => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success';
      case 'open':
        return 'bg-info';
      case 'click':
        return 'bg-primary';
      case 'sent':
      case 'processed':
      case 'created':
        return 'bg-secondary';
      case 'bounce':
      case 'deferred':
      case 'spamreport':
        return 'bg-danger';
      case 'unsubscribe':
        return 'bg-warning text-dark';
      default:
        return 'bg-dark text-white';
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(true);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedEmailTemplateId(templateId);
    const template = emailTemplates.find(t => String(t.id) === String(templateId));
    if (template) {
      setSubject(template.subject || '');
      if (template.body) {
        const contentBlock = htmlToDraft(template.body);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setSubject('');
      setEditorState(EditorState.createEmpty());
    }
  };

  const handleNewEmailSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');
    try {
      const body = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      const emailList = to.split(',').map(email => email.trim()).filter(Boolean);
      await sendBulkEmail({ 
        to: emailList, 
        subject, 
        body, 
        EmailTemplateId: selectedEmailTemplateId ? Number(selectedEmailTemplateId) : undefined 
      });
      setStatus('Email sent successfully!');
      setTo('');
      setSubject('');
      setEditorState(EditorState.createEmpty());
      setSelectedEmailTemplateId('');
      toast.success('Email sent successfully!', { position: 'top-right', autoClose: 2500 });
      setNewEmailModalOpen(false);
      fetchEmails();
    } catch (error) {
      setStatus('Failed to send email.');
      toast.error('Failed to send email.', { position: 'top-right', autoClose: 2500 });
    }
  };

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%' }} className="mt-4">
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Emails</h4>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={loading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
         
         
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchEmails}
            disabled={loading}
            title="Refresh Emails"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
          <Link to="/new-email" className="btn btn-primary d-flex align-items-center">
            <i className="bi bi-pencil-square me-2"></i>
            New Email
          </Link>
        </div>
      </div>

      {/* ==================== FILTER SIDEBAR ==================== */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${!isFilterOpen ? "d-none" : ""}`}
        onClick={() => setIsFilterOpen(false)}
        style={{ zIndex: 1040 }}
      />
      <div
        className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isFilterOpen ? "translate-end d-none" : ""}`}
        style={{ width: "500px", zIndex: 1045 }}
      >
        <div className="d-flex flex-column h-100">
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="bi bi-funnel-fill me-2"></i>Filter Email Messages
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Close"
              />
            </div>
          </div>

          <div className="p-3 flex-grow-1">
            {/* Recipient Email */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Recipient Email</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by recipient email..."
                  name="filter_recipient_search"
                  id="filter_recipient_search"
                  value={tempFilters.recipientEmail}
                  onChange={(e) => setTempFilters(prev => ({ ...prev, recipientEmail: e.target.value }))}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Sender Email */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Sender Email</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by sender email..."
                  name="filter_sender_search"
                  id="filter_sender_search"
                  value={tempFilters.senderEmail}
                  onChange={(e) => setTempFilters(prev => ({ ...prev, senderEmail: e.target.value }))}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Subject</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by subject..."
                  name="subject"
                  value={tempFilters.subject}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                />
              </div>
            </div>

            <hr className="my-4" />

            <div className="vstack gap-4">
              {/* Status */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Status</label>
                <select
                  className="form-select"
                  name="status"
                  value={tempFilters.status}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                >
                  <option value="">All</option>
                  <option value="created">Created</option>
                  <option value="processed">Processed</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="open">Open</option>
                  <option value="click">Click</option>
                  <option value="bounce">Bounce</option>
                  <option value="deferred">Deferred</option>
                  <option value="spamreport">Spam Report</option>
                  <option value="unsubscribe">Unsubscribe</option>
                </select>
              </div>

              {/* Template */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Template</label>
                <select
                  className="form-select"
                  name="emailTemplateId"
                  value={tempFilters.emailTemplateId}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                >
                  <option value="">All Templates</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.templateName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Date Range</label>
                <div className="mb-3">
                  <label className="form-label small text-muted">Created From</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="createdFrom"
                      value={tempFilters.createdFrom}
                      onChange={handleTempFilterChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label small text-muted">Created To</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="createdTo"
                      value={tempFilters.createdTo}
                      onChange={handleTempFilterChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 border-top bg-light">
            <div className="d-grid gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearAllFilters}
                disabled={loading}
              >
                <i className="bi bi-x-circle me-1"></i>Clear All Filters
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>Search / Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ==================== END FILTER SIDEBAR ==================== */}

      {loading && <div>Loading...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && (
        <>
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>
                    <button className="btn btn-link text-decoration-none text-dark p-0" onClick={() => handleSort('recipientemail')}>
                      Recipient {sortBy === 'recipientemail' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button className="btn btn-link text-decoration-none text-dark p-0" onClick={() => handleSort('senderemail')}>
                      Sender {sortBy === 'senderemail' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button className="btn btn-link text-decoration-none text-dark p-0" onClick={() => handleSort('subject')}>
                      Subject {sortBy === 'subject' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>Template</th>
                  <th>
                    <button className="btn btn-link text-decoration-none text-dark p-0" onClick={() => handleSort('status')}>
                      Status {sortBy === 'status' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>
                    <button className="btn btn-link text-decoration-none text-dark p-0" onClick={() => handleSort('createdat')}>
                      Date {sortBy === 'createdat' && (sortDescending ? '↓' : '↑')}
                    </button>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(emails) && emails.map(email => (
                  <tr key={email.id}>
                    <td>{email.recipientEmail?.address || ''}</td>
                    <td>{email.senderEmail}</td>
                    <td>{email.subject}</td>
                    <td>{email.emailTemplate?.templateName || '-'}</td>
                    <td>
                      <span className={`badge text-capitalize ${getStatusBadgeClass(email.lastStatus)}`}>{email.lastStatus}</span>
                    </td>
                    <td>{new Date(email.createdAt).toLocaleString()}</td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleViewHistory(email)} title="View History">
                          <i className="bi bi-clock-history"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handlePreviewEmail(email)} title="Preview Email">
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openBlockModal(email.recipientEmail?.address || '', email.recipientEmail?.isBlocked || false)}
                          title={email.recipientEmail?.isBlocked ? `Blocked: ${email.recipientEmail?.blockedReason || 'No reason provided'}` : "Block Email"}
                        >
                          <i className={`bi bi-${email.recipientEmail?.isBlocked ? 'unlock' : 'lock'}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <select className="form-select form-select-sm me-2" value={pageSize} onChange={handlePageSizeChange} style={{ width: 'auto' }}>
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
              <span className="text-muted">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
              </span>
            </div>
               <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        </>
      )}

      <button
        className="btn btn-primary rounded-circle position-fixed"
        style={{ bottom: '2rem', right: '2rem', width: '3.5rem', height: '3.5rem', boxShadow: '0 2px 5px rgba(0,0,0,0.2)', zIndex: 1000 }}
        onClick={() => setNewEmailModalOpen(true)}
        title="Send New Email"
      >
        <i className="bi bi-envelope-plus" style={{ fontSize: '1.5rem' }}></i>
      </button>

      {/* New Email Modal */}
      {newEmailModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send New Email</h5>
                <button type="button" className="btn-close" onClick={() => setNewEmailModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleNewEmailSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Select a Template</label>
                    <select className="form-select" value={selectedEmailTemplateId} onChange={handleTemplateChange}>
                      <option value="">-- No Template --</option>
                      {emailTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.templateName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="emailTo" className="form-label fw-semibold">Recipient Email *</label>
                    <input type="text" className="form-control" id="emailTo" placeholder="Enter recipient email" value={to} onChange={e => setTo(e.target.value)} required />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="emailSubject" className="form-label fw-semibold">Subject *</label>
                    <input type="text" className="form-control" id="emailSubject" placeholder="Enter email subject" value={subject} onChange={e => setSubject(e.target.value)} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Email Body *</label>
                    <div style={{ border: '1px solid #ced4da', borderRadius: 4, minHeight: 150 }}>
                      <Editor
                        editorState={editorState}
                        onEditorStateChange={setEditorState}
                        toolbar={{ options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'history'] }}
                        editorStyle={{ minHeight: 120, padding: 8 }}
                      />
                    </div>
                  </div>
                  {status && <div className="mb-3"><small className="text-secondary">{status}</small></div>}
                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setNewEmailModalOpen(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Send Email</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <EmailHistorySidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        emailId={sidebarEmail?.id}
        subject={sidebarEmail?.subject}
      />

      {/* Preview Modal */}
      {previewOpen && previewEmail && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Email Preview</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewOpen(false)}></button>
              </div>
              <div className="modal-body">
                <h6>Subject: {previewEmail.subject}</h6>
                <div className="mb-2"><strong>To:</strong> {previewEmail.recipientEmail?.address}</div>
                <div className="mb-2"><strong>From:</strong> {previewEmail.senderEmail}</div>
                <div className="border p-3" style={{ minHeight: 100 }}>
                  <div dangerouslySetInnerHTML={{ __html: previewEmail.htmlContent || previewEmail.plainTextContent }} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailMessages;